import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MdMyLocation } from "@react-icons/all-files/md/MdMyLocation.esm.js";
import { observer } from 'mobx-react-lite';
import isEqual from "lodash/isEqual.js";
import throttle from "lodash/throttle.js";
import { IoClose } from "@react-icons/all-files/io5/IoClose.esm.js";
import useFilter from '@unmapped/core/hooks/useFilter';
import { decorateName } from '@unmapped/core/utils';
import baseStyles from "./styles.module.css.js";
import useComponentOverride from "../../hooks/useComponentOverride.js";
import TriBarPulseSpinner from "../../spinners/TriBarPulseSpinner/index.js";
import InlineControlGroup from "../../layouts/InlineControlGroup/index.js";
import useStyleOverride from "../../hooks/useStyleOverride.js";
import useMap from "../../hooks/useMap.js";
export var GeoRadiusFilter = decorateName(observer(_ref => {
  var {
    name,
    showUnit = true,
    showUnitOption = false,
    showGeoLocateButton = true,
    shortUnitLabels = showUnitOption
  } = _ref;
  var [RadioInput, TextInput, Button] = useComponentOverride('controls/RadioInput', 'controls/TextInput', 'controls/Button');
  var styles = useStyleOverride('filters/GeoRadiusFilter', baseStyles);
  var {
    filterState,
    params,
    setFilterState,
    setOnApplySnapshot
  } = useFilter(name);
  var throttledSetValue = useMemo(() => throttle(setFilterState, 500), []);
  var [unit, setUnit] = useState(filterState ? filterState.unit : params.initialUnit);
  var [radius, setRadius] = useState(filterState ? filterState.radius : params.initialRadius);
  var inputRef = useRef(null);
  var [isGeolocated, setIsGeolocated] = useState();
  var [locating, setLocating] = useState(false);
  var [origin, setOrigin] = useState(filterState ? {
    location: filterState.origin,
    data: filterState.originData
  } : null);
  var initialRenderRef = useRef(true);
  useEffect(() => {
    if (!initialRenderRef.current) {
      if (filterState) {
        if (filterState.unit !== unit) setUnit(filterState.unit);
        if (filterState.radius !== radius) setRadius(filterState.radius);
        if (!isEqual(filterState.origin, origin === null || origin === void 0 ? void 0 : origin.location) || !isEqual(filterState.originData, origin === null || origin === void 0 ? void 0 : origin.data)) {
          setOrigin({
            data: filterState.originData,
            location: filterState.origin
          });
        }
      } else {
        setOrigin(null);
        if (inputRef.current) {
          inputRef.current.value = '';
        }
      }
    }
  }, [filterState]);
  useEffect(() => {
    if (!initialRenderRef.current) {
      if (origin) {
        if (radius) {
          throttledSetValue({
            origin: origin === null || origin === void 0 ? void 0 : origin.location,
            radius,
            unit,
            originData: origin === null || origin === void 0 ? void 0 : origin.data
          });
          if (inputRef.current) {
            inputRef.current.value = origin.data ? origin.data === 'My Location' // when geolocated, we use placeholder instead of value
            ? '' : origin.data : '';
          }
        }
      } else {
        throttledSetValue(null);
        if (inputRef.current) {
          inputRef.current.value = '';
        }
      }
    } else {
      initialRenderRef.current = false;
    }
  }, [origin, unit, radius]);
  var {
    map,
    isLoaded
  } = useMap();
  useEffect(() => {
    if (inputRef.current && isLoaded) {
      var autocomplete = new google.maps.places.Autocomplete(inputRef.current, {
        componentRestrictions: {
          country: ["us", "ca"]
        },
        fields: ['address_components', 'geometry', 'formatted_address']
      });
      autocomplete.addListener('place_changed', () => {
        var _place$geometry;
        var place = autocomplete.getPlace();
        var latlng = (_place$geometry = place.geometry) === null || _place$geometry === void 0 ? void 0 : _place$geometry.location;
        if (latlng) {
          var _place$formatted_addr;
          setOrigin({
            location: {
              lat: latlng.lat(),
              lng: latlng.lng()
            },
            data: (_place$formatted_addr = place.formatted_address) !== null && _place$formatted_addr !== void 0 ? _place$formatted_addr : ''
          });
        } else {
          setOrigin(null);
        }
      });
    }
  }, [isLoaded, !!inputRef.current]);
  var autoCompleteContainerRef = useRef(null);
  return /*#__PURE__*/React.createElement("div", {
    className: styles.GeoRadiusFilter
  }, /*#__PURE__*/React.createElement(InlineControlGroup, null, /*#__PURE__*/React.createElement("div", {
    className: styles.AutoCompleteContainer,
    ref: autoCompleteContainerRef
  }, /*#__PURE__*/React.createElement(TextInput, {
    name: "origin",
    disabled: params.disabled,
    ref: inputRef,
    placeholder: (origin === null || origin === void 0 ? void 0 : origin.data) === 'My Location' ? 'My Location' : 'Enter a Location'
  })), filterState && /*#__PURE__*/React.createElement(Button, {
    minimal: true,
    disabled: params.disabled,
    onClick: () => {
      setOrigin(null);
      if (inputRef.current) inputRef.current.value = '';
    },
    title: "Clear"
  }, /*#__PURE__*/React.createElement(IoClose, {
    size: 16
  })), showGeoLocateButton && !filterState && /*#__PURE__*/React.createElement(Button, {
    minimal: true,
    disabled: params.disabled,
    onClick: () => {
      if (navigator.geolocation) {
        setLocating(true);
        navigator.geolocation.getCurrentPosition(position => {
          var pos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setLocating(false);
          setOrigin({
            location: pos,
            data: 'My Location' //`${position.coords.latitude}, ${position.coords.longitude}`
          });
          // map && map.setCenter(pos);
        }, function () {
          setLocating(false);
          for (var _len = arguments.length, e = new Array(_len), _key = 0; _key < _len; _key++) {
            e[_key] = arguments[_key];
          }
          console.log('geolocate failed: ', e);
        });
      } else {
        console.log('geolocation not supported');
      }
    },
    title: "Clear"
  }, locating ? /*#__PURE__*/React.createElement(TriBarPulseSpinner, {
    size: 16
  }) : /*#__PURE__*/React.createElement(MdMyLocation, {
    size: 16
  }))), /*#__PURE__*/React.createElement(InlineControlGroup, null, /*#__PURE__*/React.createElement("label", {
    htmlFor: "geo-radius"
  }, "Within:"), /*#__PURE__*/React.createElement(TextInput, {
    type: "number",
    id: "geo-radius",
    name: "radius",
    value: radius,
    disabled: params.disabled,
    onChange: e => {
      var r = e.target.value;
      if (r) r = Number.parseFloat(r);
      if (typeof r === 'number' && !isNaN(r)) {
        setRadius(r);
      } else {
        setRadius(0);
      }
    }
  }), showUnit ? showUnitOption ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(RadioInput, {
    name: "geoUnit",
    id: "geo-unit-mi",
    value: "mi",
    disabled: params.disabled,
    onChange: () => setUnit('mi'),
    checked: unit === 'mi'
  }), /*#__PURE__*/React.createElement("label", {
    htmlFor: "geo-unit-mi"
  }, shortUnitLabels ? 'mi' : 'miles'), /*#__PURE__*/React.createElement(RadioInput, {
    name: "geoUnit",
    id: "geo-unit-km",
    value: "km",
    disabled: params.disabled,
    onChange: () => setUnit('km'),
    checked: unit === 'km'
  }), /*#__PURE__*/React.createElement("label", {
    htmlFor: "geo-unit-km"
  }, shortUnitLabels ? 'km' : 'kilometers')) : unit === 'mi' ? /*#__PURE__*/React.createElement("label", {
    htmlFor: "geo-unit-mi"
  }, shortUnitLabels ? 'mi' : 'miles') : /*#__PURE__*/React.createElement("label", {
    htmlFor: "geo-unit-km"
  }, shortUnitLabels ? 'km' : 'kilometers') : null));
}), 'GeoRadiusFilter');
export default GeoRadiusFilter;
//# sourceMappingURL=index.js.map